import type { DiagramDocument } from '../../core/types';
import { normalizeDocument } from '../../core/normalize';
import { validateDocument } from '../../core/validation';

export interface SyncEnvelope {
  documentId: string;
  baseRevision: string | null;
  localRevision: string;
  document: DiagramDocument;
}

export interface SyncPushResult {
  acceptedRevision: string;
  document?: DiagramDocument;
}

export interface SyncGatewayConfig {
  baseUrl: string;
  getAccessToken: () => Promise<string | null>;
  getOrganizationId?: () => string | null;
}

export class SyncGatewayError extends Error {
  readonly status: number;
  readonly code: string | null;
  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.name = 'SyncGatewayError';
    this.status = status;
    this.code = code;
  }
}

function normalizeBaseUrl(value: string): string {
  const url = new URL(value);
  if (url.protocol !== 'https:' && url.hostname !== 'localhost' && url.hostname !== '127.0.0.1') throw new Error('A sincronização exige HTTPS fora do ambiente local.');
  return url.toString().replace(/\/$/, '');
}

/**
 * Contrato preparado para o futuro servidor OWDEX Draw.
 * Nenhuma instância é criada por padrão: o editor continua 100% local até o backend ser configurado.
 */
export class SyncGateway {
  private readonly baseUrl: string;
  constructor(private readonly config: SyncGatewayConfig) {
    this.baseUrl = normalizeBaseUrl(config.baseUrl);
  }

  async push(envelope: SyncEnvelope, signal?: AbortSignal): Promise<SyncPushResult> {
    const validation = validateDocument(envelope.document);
    if (!validation.valid) throw new Error(`Documento inválido para sincronização: ${validation.errors[0] ?? 'estrutura incompatível'}`);
    const token = await this.config.getAccessToken();
    if (!token) throw new Error('Sessão de sincronização não autenticada.');

    const send = async (baseRevision: string | null) => fetch(`${this.baseUrl}/v1/projects/${encodeURIComponent(envelope.documentId)}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}`, 'if-match': baseRevision ?? '*' },
      body: JSON.stringify({ schemaVersion: 1, localRevision: envelope.localRevision, organizationId: this.config.getOrganizationId?.() ?? null, document: envelope.document }),
      signal, credentials: 'omit', cache: 'no-store',
    });

    let response = await send(envelope.baseRevision);
    let detail = '';
    let code: string | null = null;
    const readFailure = async () => {
      detail = ''; code = null;
      try {
        const payload = await response.clone().json() as { error?: unknown; code?: unknown };
        if (typeof payload.error === 'string') detail = payload.error.trim();
        if (typeof payload.code === 'string') code = payload.code.trim() || null;
      } catch { /* resposta sem JSON */ }
    };
    if (!response.ok) {
      await readFailure();
      // Estado local antigo pode guardar uma revisão de um projeto que já não existe mais no servidor.
      // Se o backend confirmar explicitamente a ausência, recriar com If-Match:* é seguro e restaura a nuvem sem perder a cópia local.
      if (response.status === 409 && /ainda não existe no servidor/i.test(detail) && envelope.baseRevision) {
        response = await send(null);
        if (!response.ok) await readFailure();
      }
    }
    if (response.status === 409 || response.status === 412) throw new SyncGatewayError(detail ? `Conflito de versão: ${detail}` : 'Conflito de versão: o servidor possui uma revisão diferente deste projeto.', response.status, code);
    if (!response.ok) throw new SyncGatewayError(detail || `Falha de sincronização (${response.status}).`, response.status, code);
    const payload = await response.json() as { revision?: unknown; document?: unknown };
    const acceptedRevision = typeof payload.revision === 'string' ? payload.revision : envelope.localRevision;
    let document: DiagramDocument | undefined;
    if (payload.document !== undefined) {
      const serverValidation = validateDocument(payload.document);
      if (!serverValidation.valid) throw new Error('O servidor retornou um documento OWDEX Draw inválido.');
      document = normalizeDocument(payload.document as DiagramDocument);
    }
    return { acceptedRevision, document };
  }
}
